import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { Campaign, IGoogleAdsKeyword, IKeywordStats } from '@feathr/blackbox';
import {
  ActionMenuCell,
  createActionMenuColumn,
  NumberCell,
  Pill,
  toast,
} from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import * as styles from './KeywordStatsColumns.css';

// Zero-safe: a zero denominator renders 0.00% / $0.00 rather than NaN (NumberCell's em dash).
function getClickThroughRate(stats: IKeywordStats): number {
  return stats.num_views ? stats.num_clicks / stats.num_views : 0;
}

function getCostPerClick(stats: IKeywordStats): number {
  return stats.num_clicks ? stats.spend / stats.num_clicks : 0;
}

function getKeywordStatsColumns(campaign: Campaign, t: TFunction): Array<ColumnDef<IKeywordStats>> {
  const keywords: IGoogleAdsKeyword[] = campaign.get('keyword_themes');

  function isNegativeKeyword(term: string): boolean {
    return keywords.some((keyword) => keyword.display_name === term && keyword.negative);
  }

  async function handleAdd(term: string): Promise<void> {
    try {
      const existingNegativeKeywords = keywords
        .filter((keyword) => keyword.negative === true)
        .map((keyword) => keyword.display_name);
      const keyword = { display_name: term, negative: true, suggestion: false };
      if (!existingNegativeKeywords.includes(keyword.display_name)) {
        campaign.set({ keyword_themes: [...keywords, keyword] });
        await campaign.patchDirty();
      }
      toast(
        t('{{keyword}} has been added as a negative keyword for the campaign.', { keyword: term }),
        { type: 'success' },
      );
    } catch {
      toast(t('Something went wrong while adding the negative keyword.'), { type: 'error' });
    }
  }

  async function handleRemove(term: string): Promise<void> {
    try {
      const newKeywords = keywords.filter(
        (keyword) => keyword.display_name !== term || !keyword.negative,
      );
      campaign.set({ keyword_themes: newKeywords });
      await campaign.patchDirty();
      toast(
        t('{{keyword}} has been removed as a negative keyword for the campaign.', {
          keyword: term,
        }),
        { type: 'success' },
      );
    } catch {
      toast(t('Something went wrong while removing the negative keyword.'), { type: 'error' });
    }
  }

  return [
    {
      id: 'keywords',
      header: t('Search Term'),
      // Search terms are the variable-length content here, so they take the leftover width.
      meta: { isFlex: true },
      size: 325,
      enableSorting: false,
      cell: ({ row }: CellContext<IKeywordStats, unknown>): JSX.Element => (
        <>
          {row.original.term}{' '}
          {isNegativeKeyword(row.original.term) && (
            <Pill className={styles.negativeChip} theme={'red'}>
              {t('Negative')}
            </Pill>
          )}
        </>
      ),
    },
    {
      id: 'views',
      header: t('Views'),
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<IKeywordStats, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.num_views} />
      ),
    },
    {
      id: 'clicks',
      header: t('Clicks'),
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<IKeywordStats, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.num_clicks} />
      ),
    },
    {
      id: 'clickThroughRate',
      header: t('Click Through Rate'),
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<IKeywordStats, unknown>): JSX.Element => (
        <NumberCell format={'percent'} value={getClickThroughRate(row.original)} />
      ),
    },
    {
      id: 'costPerClick',
      header: t('Cost Per Click'),
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<IKeywordStats, unknown>): JSX.Element => (
        <NumberCell format={'currency'} value={getCostPerClick(row.original)} />
      ),
    },
    createActionMenuColumn<IKeywordStats>((row) => {
      const isNegative = isNegativeKeyword(row.term);
      return [
        !isNegative && (
          <ActionMenuCell.Item
            key={'add'}
            onClick={(): Promise<void> => handleAdd(row.term)}
            prefix={getIconForAction('add')}
          >
            {t('Add negative keyword')}
          </ActionMenuCell.Item>
        ),
        isNegative && (
          <ActionMenuCell.Item
            key={'remove'}
            onClick={(): Promise<void> => handleRemove(row.term)}
            prefix={getIconForAction('remove')}
            theme={'danger'}
          >
            {t('Remove negative keyword')}
          </ActionMenuCell.Item>
        ),
        <ActionMenuCell.Item
          key={'edit'}
          link={`${campaign.getItemUrl()}/edit#step3`}
          prefix={getIconForAction('edit')}
        >
          {t('Edit keyword themes')}
        </ActionMenuCell.Item>,
      ];
    }),
  ];
}

export default getKeywordStatsColumns;
